import { UserAgentInfo } from '../../core/utils/user-agent';
import { IComment, Icons } from '@tecalisdev/infinity';

export interface IProcessDeviceView {
	uuid: string;
	user_agent: string;
	user_agent_info?: UserAgentInfo;
	device_model?: string;
	created_at?: string;
	is_primary: boolean;
	ip?: string;
}

export interface IProcessLocationView {
	lat: string;
	lon: string;
}

/** @deprecated Usar el mapeo en ProcessDevicesTileDirective (`tec-comment` thread + `moreInfo.infoGroups`). */
export interface IProcessDeviceRowView {
	id: string;
	device: IProcessDeviceView;
	index: number;
	comment: IComment;
}

export function deviceDisplayName(translateFn: (key: string) => string, index: number): string {
	return `${translateFn('PROCESSES.DETAIL.DEVICE')} ${index + 1}`;
}

export function deviceBrowserSummary(device: IProcessDeviceView): string {
	if (device.user_agent_info) {
		const base = `${device.user_agent_info.browser} (v${device.user_agent_info.version}) ${device.user_agent_info.os}`;
		return device.device_model ? `${base} - ${device.device_model}` : base;
	}
	const ua = device.user_agent || '';
	return ua.length > 40 ? `${ua.substring(0, 40)}...` : ua || '-';
}

export function deviceTypeIcon(device: IProcessDeviceView): Icons {
	return device.user_agent_info?.deviceType === 'Desktop' ? 'icon-tv' : 'icon-smartphone';
}

/**
 * Empareja cada localización con un dispositivo real.
 * Sin `device_id` en location (API actual): índice si hay dispositivo; si hay más
 * localizaciones que dispositivos, se reutiliza el último dispositivo (no se inventan nº).
 */
export function resolveDeviceIndexForLocation(locationIndex: number, devicesCount: number): number | null {
	if (devicesCount <= 0) {
		return null;
	}
	return Math.min(locationIndex, devicesCount - 1);
}

/** Estado del candado IP en tile/mapa (NIF-353). Por IP del dispositivo + permisos. */
export type DevicesIpBlockUnlockMode = 'block' | 'unlock' | null;

export interface ProcessDeviceBlockUnlockEvent {
	action: Exclude<DevicesIpBlockUnlockMode, null>;
	device: IProcessDeviceView;
}

export function isDeviceIpBlocked(
	deviceIp: string | undefined | null,
	blockedIps: { ip?: string }[] | undefined | null,
): boolean {
	const ip = deviceIp?.trim();
	if (!ip) {
		return false;
	}
	return (blockedIps ?? []).some((entry) => entry.ip?.trim() === ip);
}

/**
 * Candado por dispositivo:
 * - IP de ese dispositivo en `blocked_ips` → unlock (si permiso)
 * - IP libre → block (si permiso)
 * - sin IP → null
 */
export function devicesIpBlockUnlockMode(
	deviceIp: string | undefined | null,
	blockedIps: { ip?: string }[] | undefined | null,
	canBlock: boolean,
	canUnblock: boolean,
): DevicesIpBlockUnlockMode {
	if (!deviceIp?.trim()) {
		return null;
	}
	if (isDeviceIpBlocked(deviceIp, blockedIps)) {
		return canUnblock ? 'unlock' : null;
	}
	return canBlock ? 'block' : null;
}

/** Conservado para tests de correlación nombre/browser; la UI usa el tile. */
export function mapProcessDeviceToCommentRow(
	device: IProcessDeviceView,
	index: number,
	translateFn: (key: string) => string,
): IProcessDeviceRowView {
	const name = deviceDisplayName(translateFn, index);
	const browser = deviceBrowserSummary(device);
	const ip = device.ip?.trim() ? device.ip : '-';

	const comment: IComment = {
		id: device.uuid || `device-${index}`,
		timestamp: 0,
		infoGroup: {
			label: name,
		},
		moreInfo: {
			infoGroups: [
				{
					bold: translateFn('PROCESSES.DETAIL.BROWSER'),
					text: browser,
				},
				{
					bold: 'IP',
					text: ip,
				},
			],
		},
	};

	return {
		id: comment.id,
		device,
		index,
		comment,
	};
}
